<template>
  <PageHeader :showBreadcrumb="true" />
  <p>
    Please refer to the Guidelines outlined under PBM Licensing, which can be found under
    <a href="https://mn.gov/commerce/licensing/list/insurance/licensing.jsp#2)">Network Adequacy Report</a> section.
    All templates can be downloaded from this section. Please make sure you are using the most up to date template.
  </p>
  <p>
    <b>PBM Network Template: </b>
    One template per Pharmacy Benefit Manager is required. Please submit with your first network.
  </p>
  <p>
    <b>PBM Provider File Template: </b>
    You need to submit separate Provider File for each network.
  </p>
  <p>
    <b>PBM Network Adequacy attestation: </b>
    You need to submit separate Network Adequacy Attestation form(s) for each network.
  </p>
  <p>
    <b>PBM Geographic access Maps: </b>
    You need to submit a separate set of Geographic Access Map(s) for each network. Please make sure to provide a map identifying all counties in the service area with no providers.
    Retail pharmacy locations must be measured against the 60 minutes/60 miles standard; please use miles or minutes, not radius.
  </p>
  <p>
    <b>PBM Network Waiver: </b>
    Required if retail pharmacy access does not meet the lesser of 60 minutes/60 miles standard. Submit a separate waiver request for each network which has retail pharmacy service gaps.
  </p>
  <p>
    <b>PBM Rental Agreement: </b>
    Please upload the completely executed network rental agreement (if applicable) between the network contracts.
  </p>
  <hr/>
  <div class="container p-3 text-center">
    <!-- Submit form to include entity and network info -->
    <form @submit.stop.prevent="onSubmit">
      <!-- Display the user entities -->
      <div class="row mb-2 g-3">
        <div class="col-lg-12 col-md-12">
          <FormSelectGroup v-if="userEntityAssigned"
                           label="Select PBM"
                           inputId="pbm-input"
                           :required="true"
                           state>
            <template #default="">
              <PvSelect id="pbm-input"
                        v-model="selectedPBM"
                        :options="userPBMOptions"
                        @change="onChangePBM"
                        ariaLabel="pbm-input-label"
                        placeholder="-- Select PBM --"
                        optionLabel="name"
                        optionValue="id"
                        autoOptionFocus="true"/>
            </template>
          </FormSelectGroup>
        </div>
      </div>
      <!-- Display the Network list and ID -->
      <p v-if="selectedPBM && selectedPBMNetwork" class="fw-bold">
        Note: Please select another PBM network from the drop-down, if you like to upload form(s) for that network.
      </p>
      <div class="row mb-2 g-3">
        <div class="col-lg-12 col-md-12">
          <FormSelectGroup v-if="selectedPBM"
                           label="Select Network"
                           inputId="network-input"
                           :required="true"
                           state>
            <template #default="">
              <PvSelect id="network-input"
                        v-model="selectedPBMNetwork"
                        :options="userPBMNetworkOptions"
                        @change="onChangeNetwork"
                        ariaLabel="network-input-label"
                        placeholder="-- Select Network --"
                        :optionLabel="formatNetworkLabel"
                        optionValue="id"
                        autoOptionFocus="true"/>
            </template>
          </FormSelectGroup>
        </div>
        <!-- Add a new PBM Network -->
        <div v-if="isAddingNetwork" class="text-start mt-2">
          <label for="new-network-name" class="form-label">New PBM Network Name</label>
          <InputText id="new-network-name"
                     type="text"
                     v-model="formState.state.data.newNetworkName"
                     class="form-control mb-2"
                     placeholder="Enter network name" />
          <Button type="submit" class="btn btn-primary" label="Add Network" />
        </div>
      </div>
    </form>
    <!-- Display the username(email) and fullName -->
    <div v-if="selectedPBM && selectedPBMNetwork" class="row mb-2 g-3">
      <div v-if="!isAddingNetwork" class="col-lg-4 col-md-12">
        <ReadOnlyText label="Network ID" :value="appStore.networks.find(n => n.id === selectedPBMNetwork).networkId" />
      </div>
      <div class="col-lg-4 col-md-12">
        <ReadOnlyText v-if="store.user" label="Full Name" :value="store.user.firstName + ' ' + store.user.lastName" />
      </div>
      <div class="col-lg-4 col-md-12">
        <ReadOnlyText v-if="store.user" label="User Name" :value="store.user.email" />
      </div>
    </div>
    <hr/>
    <!-- Documents Upload/Delete Section -->
    <div v-if="selectedPBM && selectedPBMNetwork && !isAddingNetwork" class="row">
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="NetworkAdequacy" label="PBM Network Template Upload" ref="pbmNetworkTemplate" name="pbmNetworkTemplate" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12" url="/undefined" :entity="selectedPBM" :network="selectedPBMNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="NetworkAdequacy" label="PBM Provider File Upload" ref="pbmProviderFile" name="pbmProviderFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12" url="/undefined" :entity="selectedPBM" :network="selectedPBMNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="NetworkAdequacy" label="PBM Network Adequacy Attestation Upload" ref="pbmNetworkAdequacyAttestation" name="pbmNetworkAdequacyAttestation" accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" url="/undefined" :entity="selectedPBM" :network="selectedPBMNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="NetworkAdequacy" label="PBM Geographic Access Map Upload" ref="pbmGeographicAccessMap" name="pbmGeographicAccessMap" accept="application/pdf" url="/undefined" :entity="selectedPBM" :network="selectedPBMNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="NetworkAdequacy" label="PBM Network Waiver Upload" ref="pbmNetworkWaiver" name="pbmNetworkWaiver" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12" url="/undefined" :entity="selectedPBM" :network="selectedPBMNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="NetworkAdequacy" label="PBM Rental Agreement Upload" ref="pbmRentalAgreement" name="pbmRentalAgreement" accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf" url="/undefined" :entity="selectedPBM" :network="selectedPBMNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="NetworkAdequacy" label="Other File Upload" ref="otherFile" name="otherFile" accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf" url="/undefined" :entity="selectedPBM" :network="selectedPBMNetwork"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import PageHeader from "@/components/common/PageHeader.vue";
import MNFileUpload from "@/components/common/MNFileUpload.vue";
import FormSelectGroup from "@/components/common/FormSelectGroup.vue";
import {useAppStore} from "@/stores/appStore.js";
import {ENTITY_TYPES} from "@/utils/constants.js";
import {computed, ref} from "vue";
import {useAuthStore} from "@/stores/authStore.js";
import ReadOnlyText from "@/components/common/ReadOnlyText.vue";
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import {useForm} from "@/composables/form.js";
import {RegisterNetworkModel} from "@/models/RegisterNetworkModel.js";

const store = useAuthStore();
const appStore = useAppStore();
const selectedPBM = ref();
const selectedPBMNetwork = ref();

const pbmNetworkAdequacyAttestation = ref();
const pbmProviderFile = ref();
const pbmGeographicAccessMap = ref();
const pbmNetworkWaiver = ref();
const pbmNetworkTemplate = ref();
const pbmRentalAgreement = ref();
const otherFile = ref();

const newNetworkName = ref('');

const isAddingNetwork = ref(false);

const formState = useForm(new RegisterNetworkModel(), RegisterNetworkModel.getPBMValidationRules());

const userPBMOptions = computed(() => {
  return appStore.getUserEntitiesByTypes([ENTITY_TYPES.PBM]);
});

const userPBMNetworkOptions = computed(() => {
  const base = appStore.getNetworksByEntityId(selectedPBM.value) || [];
  return [
    ...base,
    { id: 'add-new', networkName: 'Not In the list', networkId: '' }
  ];
});

const userEntityAssigned = computed(() => {
  return userPBMOptions.value && userPBMOptions.value.length > 0;
});

const onChangePBM = async ({originalEvent, value}) => {
  selectedPBMNetwork.value = null;
  formState.state.data.entityId = value;
  await clearUploaders();
};

const onChangeNetwork = async ({originalEvent, value}) => {
  if (value === 'add-new') {
    isAddingNetwork.value = true;
    formState.state.data.newNetworkName = '';
    formState.state.data.newNetworkId = 'TBD';
    formState.state.data.newMarketType = 'TBD';
  }
  else {
    console.log('Selected Network: ', selectedPBMNetwork.value);
    isAddingNetwork.value = false;
    await clearUploaders();
  }
};

const formatNetworkLabel = (option) => {
  if (!option) return '';
  return `${option.networkName} (${option.networkId})`;
};

const clearUploaders = async () => {
  pbmNetworkAdequacyAttestation.value?.clear();
  pbmProviderFile.value?.clear();
  pbmGeographicAccessMap.value?.clear();
  pbmNetworkWaiver.value?.clear();
  pbmNetworkTemplate.value?.clear();
  pbmRentalAgreement.value?.clear();
  otherFile.value?.clear();
};

const onSubmit = async () => {
  formState.state.$v.$touch();
  if (formState.state.$v.$invalid) {
    console.log('formState state is invalid!');
    nextTick(() => formState.focusInvalidInput());
  } else {
    // Save the registration
    console.log('formState data is ready to be saved: ', formState.state.data);
    await formState.save(() => formState.state.data.save());
  }
};
</script>

<style scoped>

</style>